import * as React from 'react';
import { colors, radii } from '@app/components/designTokens';
import { DaySchedule, DAY_ORDER, DAY_ABBR } from '@app/Schedules/types';

/** Index a driver's day list by day-of-week for O(1) lookup. */
export function byDay(schedules: DaySchedule[] | undefined): Record<string, DaySchedule> {
  const map: Record<string, DaySchedule> = {};
  (schedules || []).forEach(d => {
    map[d.dayOfWeek] = d;
  });
  return map;
}

/** A day counts as "on" only when it's active AND has at least one time block. */
export function dayActive(d: DaySchedule | undefined): boolean {
  return !!d && d.isActive && d.timeSlots.length > 0;
}

/** "08:00–10:00, 15:00–19:00" for an active day, otherwise "off". */
export function slotLabel(d: DaySchedule | undefined): string {
  if (!dayActive(d)) return 'off';
  return d!.timeSlots.map(s => `${s.startTime}–${s.endTime}`).join(', ');
}

/** A single ●/○ cell for the all-drivers matrix. */
export const DayDot: React.FunctionComponent<{ on: boolean; title?: string }> = ({ on, title }) => (
  <span
    title={title}
    style={{
      display: 'inline-block',
      width: 13,
      height: 13,
      borderRadius: '50%',
      background: on ? colors.success : 'transparent',
      border: on ? 'none' : `1.5px solid ${colors.border}`,
      verticalAlign: 'middle'
    }}
  />
);

/** Detail variant for the driver page: every day with its time blocks (or "Off"). */
export const WeekScheduleDetail: React.FunctionComponent<{ schedules: DaySchedule[] }> = ({
  schedules
}) => {
  const map = byDay(schedules);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {DAY_ORDER.map(day => {
        const d = map[day];
        const on = dayActive(d);
        return (
          <div
            key={day}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '10px 14px',
              borderRadius: radii.input,
              background: on ? 'rgba(5, 150, 105, 0.06)' : colors.bgMuted
            }}
          >
            <DayDot on={on} />
            <span
              style={{
                width: 42,
                fontSize: 13,
                fontWeight: 700,
                color: on ? colors.title : colors.subtle
              }}
            >
              {DAY_ABBR[day]}
            </span>
            {on ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {d!.timeSlots.map((s, i) => (
                  <span
                    key={s.id ?? i}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: colors.body,
                      background: '#fff',
                      border: `1px solid ${colors.border}`,
                      borderRadius: radii.badge,
                      padding: '3px 10px'
                    }}
                  >
                    {s.startTime}–{s.endTime}
                  </span>
                ))}
              </div>
            ) : (
              <span style={{ fontSize: 13, color: colors.placeholder }}>Off</span>
            )}
          </div>
        );
      })}
    </div>
  );
};
